import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, clearSession, getStoredToken, getStoredUser, storeSession } from '../lib/api';

const AuthContext = createContext(null);

function parseIPv4Prefix(cidr) {
  const [addressText, bitsText, extra] = String(cidr || '').split('/');
  const octets = addressText?.split('.').map(Number);
  const bits = Number(bitsText);
  if (extra !== undefined || octets?.length !== 4 || octets.some((part) => !Number.isInteger(part) || part < 0 || part > 255) || !Number.isInteger(bits) || bits < 0 || bits > 32) return null;
  const address = octets.reduce((value, part) => (value << 8n) | BigInt(part), 0n);
  const mask = bits === 0 ? 0n : (0xffffffffn << BigInt(32 - bits)) & 0xffffffffn;
  return { address, network: address & mask, bits };
}

export function subnetContains(parentCIDR, childCIDR, strict = false) {
  const parent = parseIPv4Prefix(parentCIDR);
  const child = parseIPv4Prefix(childCIDR);
  if (!parent || !child || child.address !== child.network || parent.bits > child.bits || (strict && parent.bits === child.bits)) return false;
  const mask = parent.bits === 0 ? 0n : (0xffffffffn << BigInt(32 - parent.bits)) & 0xffffffffn;
  return (child.network & mask) === parent.network;
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(() => getStoredUser());
  const [authenticated, setAuthenticated] = useState(() => Boolean(getStoredToken() && getStoredUser()));

  const logout = useCallback(() => {
    clearSession();
    setAuthenticated(false);
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    window.addEventListener('cluster:unauthorized', logout);
    return () => window.removeEventListener('cluster:unauthorized', logout);
  }, [logout]);

  useEffect(() => {
    if (!authenticated) return;
    api.get('/api/v1/auth/me').then((nextUser) => {
      storeSession(getStoredToken(), nextUser);
      setUser(nextUser);
    }).catch(() => {});
  }, [authenticated]);

  const login = useCallback(async (username, password) => {
    const result = await api.post('/api/v1/auth/login', { username, password });
    const token = result.token || result.jwt || result.access_token;
    const nextUser = result.user;
    if (!token || !nextUser) throw new Error('Invalid login response');
    storeSession(token, nextUser);
    setUser(nextUser);
    setAuthenticated(true);
    return nextUser;
  }, []);

  const value = useMemo(() => ({
    user,
    authenticated,
    login,
    logout,
    isAdmin: user?.role === 'admin',
    canViewSubnet: (cidr) => user?.role === 'admin' || (user?.subnets || []).some((item) => subnetContains(item.subnet_cidr, cidr)),
    canEditSubnet: (cidr) => user?.role === 'admin' || (user?.subnets || []).some((item) => item.can_edit && subnetContains(item.subnet_cidr, cidr)),
    canCreateChildSubnet: (cidr) => user?.role === 'admin' || (user?.subnets || []).some((item) => item.can_edit && item.can_create_children && subnetContains(item.subnet_cidr, cidr, true)),
    canCreateSubnets: user?.role === 'admin' || (user?.subnets || []).some((item) => item.can_edit && item.can_create_children),
  }), [authenticated, login, logout, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
